import { useState, useEffect } from 'react';
import axios from 'axios';
import { Check, Calendar } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import BookingZone from '@/components/BookingZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AbonnementPage = () => {
  const [pageSettings, setPageSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, servicesRes] = await Promise.all([
          axios.get(`${API}/pages/abonnement`),
          axios.get(`${API}/services`)
        ]);
        setPageSettings(pageRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const benefits = [
    "Intervention régulière programmée",
    "Tarif préférentiel garanti",
    "Pas d'engagement longue durée",
    "Priorité de passage",
    "Conseils personnalisés",
    "Rapport d'intervention détaillé"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16 lg:py-24 pt-32 lg:pt-40" data-testid="abonnement-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-emerald-100 text-emerald-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Calendar className="w-4 h-4 inline mr-2" />
              Entretien régulier
            </span>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-stone-900 mb-6">
              {pageSettings?.title || "Créer mon abonnement jardin"}
            </h1>
            <p className="text-stone-600 text-lg">
              {pageSettings?.description || "Optez pour un entretien régulier et professionnel de votre jardin. Choisissez la formule qui vous convient."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-16" data-testid="abonnement-content">
        <div className="container-custom">
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Booking Zone - Appears FIRST on mobile */}
            <div className="lg:order-2 bg-white rounded-3xl shadow-xl border border-stone-100 p-4 sm:p-6 lg:p-8 lg:sticky lg:top-24 lg:self-start">
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-4 sm:mb-6 text-center">
                Réserver mon abonnement
              </h2>
              <div className="iframe-container">
                <BookingZone 
                  embedHtml={pageSettings?.embed_html} 
                  placeholder="Le formulaire de réservation d'abonnement sera affiché ici"
                />
              </div>
            </div>

            {/* Benefits - Appears SECOND on mobile */}
            <div className="lg:order-1">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Les avantages de l'abonnement
              </h2>
              <ul className="space-y-4 mb-8">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <span className="text-stone-700">{benefit}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-heading font-semibold text-xl text-stone-900 mb-4">
                Services inclus
              </h3>
              <div className="grid grid-cols-2 gap-3">
                {services.slice(0, 6).map((service) => (
                  <div 
                    key={service.id}
                    className="bg-stone-50 rounded-xl p-4 border border-stone-100"
                  >
                    <p className="font-medium text-stone-900 text-sm">{service.title}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ or Additional Info */}
      <section className="py-16 bg-stone-50" data-testid="abonnement-faq">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading font-bold text-2xl text-stone-900 mb-4">
              Des questions ?
            </h2>
            <p className="text-stone-600 mb-6">
              Notre équipe est à votre disposition pour répondre à toutes vos questions 
              et vous aider à choisir la formule la plus adaptée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:0465011022" 
                className="btn-primary inline-flex items-center justify-center"
              >
                Appelez-nous : 04 65 01 10 22
              </a>
              <a 
                href="mailto:contact@jardily.fr" 
                className="btn-secondary inline-flex items-center justify-center"
              >
                contact@jardily.fr
              </a>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AbonnementPage;
